<template>
  <div
    class="hcl-list"
    v-bind="attributesMeta(props.definition?.meta)"
  >
    <div
      class="list-header"
      v-if="componentsToolbars(props.definition.header).length > 0"
    >
      <component
        v-for="header in props.definition.header"
        :is="componentMeta(header.meta)"
        :key="header.meta.id"
        :definition="header"
        :data="toolbarData"
        :config="props.definition.content[0].configuration"
        v-on:hcl-button-clicked="onButtonClicked"
        v-on:hcl-list-global-select-all-changed="onListGlobalSelectAllChanged"
      >
      </component>
    </div>
    <div class="list-content">
      <div class="content-wrapper">
        <table
          class="wrapper-table table table-sm"
          :class="{ 'table-hover': !props.embedded, 'table-embedded': props.embedded }"
        >
          <thead
            class="table-header"
            v-if="columnHeaderShowHeader"
          >
            <draggable
              v-model="columnsRef"
              @start="columnHeaderOnDragStart"
              :move="columnHeaderOnDragMove"
              @end="columnHeaderOnDragEnd"
              item-key="name"
              tag="tr"
              class="header-row"
            >
              <template #item="{ element }">
                <component
                  :is="columnHeaderComponentName(element)"
                  :key="element.name"
                  v-if="columnVisible(element)"
                  :column="element"
                  :config="props.definition.content[0].configuration"
                  :selection="bucketSelection"
                  :pager="pager"
                  :sorting="columnSorting(element)"
                  v-on:hcl-column-header-sort-change="columnHeaderOnSortChange"
                  v-on:hcl-column-header-checkbox-selection-change="columnHeaderOnCheckboxSelectionChange"
                ></component>
              </template>
            </draggable>
          </thead>
          <template v-if="loading">
            <tbody
              class="table-body"
              v-if="rowsPaged.length > 0"
            >
              <template
                v-for="row of rowsPaged"
                :key="row.entities[0].id"
              >
                <hcl-list-row-skeleton
                  minWidth="100"
                  :height="getCacheSkeletonRowHeight(row.entities[0].id, '0')"
                ></hcl-list-row-skeleton>
                <template
                  v-for="sub in rowSubDefinitions"
                  :key="sub.index"
                >
                  <hcl-list-row-skeleton
                    v-if="sub.index in getCacheSkeletonRowEntities(row.entities[0].id)"
                    minWidth="100"
                    :height="getCacheSkeletonRowHeight(row.entities[0].id, sub.index)"
                  ></hcl-list-row-skeleton>
                </template>
              </template>
            </tbody>
            <tbody
              class="table-body"
              v-if="rowsPaged.length === 0"
            >
              <hcl-list-row-skeleton
                minWidth="100"
                :height="getCacheSkeletonRowHeight('0', '0')"
              ></hcl-list-row-skeleton>
            </tbody>
          </template>
          <template v-else>
            <tbody
              class="table-body"
              v-if="!rowMessageSuppress && bucket.active && pager.rowsCount === 0"
            >
              <tr class="body-row table-info">
                <td colspan="999">{{ rowMessageNoRowsSearchActive }}</td>
              </tr>
            </tbody>
            <tbody
              class="table-body"
              v-if="!rowMessageSuppress && !bucket.active && pager.rowsCount === 0"
            >
              <tr class="body-row table-light">
                <td colspan="999">{{ rowMessageNoRowsSearchInactive }}</td>
              </tr>
            </tbody>
            <tbody
              class="table-body"
              v-if="pager.rowsCount > 0"
            >
              <template v-for="(row, rowIndex) in rowsPaged">
                <tr
                  class="body-row"
                  v-if="rowIsVisible(row.entities[0], 0)"
                  :class="rowClasses(row.entities[0], 0)"
                  v-bind="row.entities[0].entry.attributes"
                  :data-row-id="row.entities[0].id"
                  data-row-index="0"
                  ref="refSkeletonRows"
                >
                  <template v-for="column in columnsRef">
                    <template v-if="column.type === 'stack'">
                      <td
                        :key="columnKey(column)"
                        v-if="columnVisible2(column)"
                        :class="columnClass(column)"
                      >
                        <div>
                          <template v-for="item in column.items">
                            <hcl-list-row-column-body
                              v-if="columnVisible2(item)"
                              :key="item.name + '-' + rowIndex + '-' + item.index"
                              :column="item"
                              :config="props.definition.content[0].configuration"
                              :lookups="bucket.lookups"
                              :entry="row.entities[0].entry"
                              :selection="bucketSelection"
                              :id="row.entities[0].id"
                              :index="0"
                              :columncount="columnsVisibleCount"
                              :actionscount="rowMaxActionsCount"
                              v-on:hcl-list-row-checkbox-changed="onListRowCheckboxChanged"
                            >
                            </hcl-list-row-column-body>
                          </template>
                        </div>
                      </td>
                    </template>
                    <template v-else>
                      <td
                        :key="columnKey(column)"
                        v-if="columnVisible2(column)"
                        :class="columnClass(column)"
                      >
                        <hcl-list-row-column-body
                          :key="column.name + '-' + rowIndex"
                          :column="column"
                          :config="props.definition.content[0].configuration"
                          :lookups="bucket.lookups"
                          :entry="row.entities[0].entry"
                          :selection="bucketSelection"
                          :id="row.entities[0].id"
                          :index="0"
                          :columncount="columnsVisibleCount"
                          :actionscount="rowMaxActionsCount"
                          v-on:hcl-list-row-checkbox-changed="onListRowCheckboxChanged"
                        >
                        </hcl-list-row-column-body>
                      </td>
                    </template>
                  </template>
                  <td
                    v-if="collapsibleButtonShow(row)"
                    class="row-column"
                  >
                    <div class="wrapper">
                      <div class="column-collapsible">
                        <a
                          type="button"
                          class="btn float-right"
                          @click.prevent="columnBodyCollapsibleOnToggle({ id: row.id, index: 0 })"
                          :title="collapsibleButtonStateIsOpen(row) ? collapsibleButtonStateIsOpenMessage : collapsibleButtonStateIsClosedMessage"
                        >
                          <i
                            v-if="collapsibleButtonStateIsOpen(row)"
                            class="eii--fa6-solid--square-minus"
                          ></i>
                          <i
                            v-if="!collapsibleButtonStateIsOpen(row)"
                            class="eii--fa6-solid--square-plus"
                          ></i>
                        </a>
                      </div>
                    </div>
                  </td>
                </tr>
                <template v-for="sub in rowSubDefinitions">
                  <tr
                    class="body-row"
                    :class="rowClasses(row.entities[sub.index], sub.index)"
                    v-if="rowIsVisible(row.entities[sub.index], sub.index)"
                    v-bind="row.entities[sub.index].entry.attributes"
                    :data-row-id="row.entities[0].id"
                    :data-row-index="sub.index"
                    ref="refSkeletonRows"
                  >
                    <component
                      :is="rowComponentName(props.definition.content[sub.index])"
                      :key="row.entities[sub.index].id"
                      :id="row.entities[sub.index].id"
                      :index="sub.index"
                      :columns="props.definition.content[sub.index].columns"
                      :config="props.definition.content[sub.index].configuration"
                      :lookups="bucket.lookups"
                      :entry="row.entities[sub.index].entry"
                      :selection="selection"
                      :columncount="columnsVisibleCount"
                      :entity="sub.entity"
                      v-on:hcl-list-row-column-body-collapsible-clicked="columnBodyCollapsibleOnToggle"
                      v-on:hcl-list-embedded-updated="onHclListEmbeddedUpdated"
                    ></component>
                  </tr>
                </template>
              </template>
            </tbody>
          </template>
        </table>
      </div>
    </div>
    <div
      class="list-footer"
      v-if="componentsToolbars(props.definition.footer).length > 0"
    >
      <component
        v-for="footer in props.definition.footer"
        :is="componentMeta(footer.meta)"
        :key="footer.meta.id"
        :definition="footer"
        :data="toolbarData"
        :config="props.definition.content[0].configuration"
        v-on:hcl-button-clicked="onButtonClicked"
        v-on:hcl-list-global-select-all-changed="onListGlobalSelectAllChanged"
      >
      </component>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, Ref, computed, watch, inject, provide, nextTick, onUpdated } from 'vue'

import { useMeta } from '@/composables/meta'

import { useDataStore } from '@/stores/data'
import { useStateStore } from '@/stores/state'

import type { DefinitionList, DefinitionListContentColumn, DefinitionListContentConfigurationSelection, DataList, DataListRow, DataListRowEntity, DefinitionToolbar, EventColumnCheckboxToggle, EventButtonClicked, EventListGlobalSelectAllChanged } from '@/components/types'

import draggable from 'vuedraggable'

import { usePager } from '@/components/Pager/pager'

import { useCollapsibleList } from './collapsible'
import { useColumns } from './columns'

import HclListRowEmpty from '@/components/ListRowEmpty/ListRowEmpty.vue'
import HclListRowHtml from '@/components/ListRowHtml/ListRowHtml.vue'
import HclListRowList from '@/components/ListRowList/ListRowList.vue'
import HclListRowColumnBody from '@/components/ListRowColumnBody/ListRowColumnBody.vue'
import HclListRowSkeleton from '@/components/ListRowSkeleton/ListRowSkeleton.vue'

import { useActionLists } from '../ToolbarContentActionSelect/actionlists'

interface Props {
  /**
   * Definition as an JS object.
   */
  definition: DefinitionList
  /**
   * Whether or not the List is embedded in a List.
   */
  embedded?: boolean
  /**
   * Action List ID of Parent List
   */
  actionlistid?: string
}

const props = withDefaults(defineProps<Props>(), {
  definition: undefined,
  embedded: false,
  actionlistid: undefined
})

provide('listMetaId', props.definition.meta.id ?? '')

const emit = defineEmits(['hcl-button-clicked', 'hcl-list-embedded-updated'])

const { componentMeta, attributesMeta } = useMeta()

const { link } = useDataStore()

const bucket: Ref<DataList> = link(props.definition.meta.id)

const bucketSelection: Ref<DefinitionListContentConfigurationSelection | undefined> = ref(props.definition.content[0].configuration.selection)

const globalSelectAll = ref(false)

const updateSelectionfromBucket = () => {
  bucketSelection.value = bucket.value.selection

  if (!('global' in bucketSelection.value)) {
    bucketSelection.value.global = {
      show: false,
      selected: false
    }
  }

  // @ts-ignore
  if ('selected' in bucketSelection.value.global) {
    bucketSelection.value.global.selected = globalSelectAll.value
  }

  if (globalSelectAll.value) {
    bucketSelection.value.header.selected = true
    updateListSelection('on')
  }
}

/*
 * Skeleton Handling
 */
const refSkeletonRows = ref()

const cacheSkeletonRowsHeights = ref()

const generateCacheSkeletonRowsHeights = (rows: HTMLElement[]) => {

  if (rows === undefined) return

  const t = {}

  for (const row of rows) {
    const rowId = row.getAttribute('data-row-id')
    const rowIndex = row.getAttribute('data-row-index')

    if (rowId === null || rowIndex === null) break

    if (!(rowId in t)) t[rowId] = {}

    t[rowId][rowIndex] = row.offsetHeight
  }

  cacheSkeletonRowsHeights.value = t

}

const getCacheSkeletonRowEntities = (rowId: string) => {
  return rowId in cacheSkeletonRowsHeights.value ? Object.keys(cacheSkeletonRowsHeights.value[rowId]) : ['0']
}

const getCacheSkeletonRowHeight = (rowId: string, entityId: string) => {

  // height of 38 is an empirical value for a row with only the text 'no results found'

  if (cacheSkeletonRowsHeights.value === undefined) return 38

  if (rowId in cacheSkeletonRowsHeights.value && entityId in cacheSkeletonRowsHeights.value[rowId]) {
    return cacheSkeletonRowsHeights.value[rowId][entityId]
  }

  return 38

}

generateCacheSkeletonRowsHeights(refSkeletonRows.value)

watch(
  refSkeletonRows,
  (n, o) => {
    if (n.length > 0) {
      generateCacheSkeletonRowsHeights(n)
    }
  },
  {
    deep: true
  }
)

onUpdated(() => {
  if (props.embedded === true) {
    emit('hcl-list-embedded-updated')
  }
})

/*
 * Pager Handling
 */
const {
  pager,
  pagerUpdatePageCurrent,
  pagerUpdateRowsPerPage,
  pagerUpdateRowsCount,
  pagerUpdateCriteria
} = usePager(bucket.value?.rows?.length ?? 0)

const updatePagerFromBucket = () => {
  pagerUpdateRowsCount(bucket.value.pagination.rowsCount)
  pagerUpdateRowsPerPage(bucket.value.pagination.rowsPerPage)
  pagerUpdatePageCurrent(bucket.value.pagination.pageCurrent)
  if ('criteria' in bucket.value.pagination) {
    pagerUpdateCriteria(bucket.value.pagination.criteria)
  }
}

if (bucket.value?.rows?.length > 0) {
  updatePagerFromBucket()
  updateSelectionfromBucket()
}

/*
 * Bucket Handling
 */
const selection = ref(props.definition.content[0].configuration.selection)

const triggerWidgetReparse = () => {
  if (typeof $ !== 'undefined') {
    $(document).trigger('hcl.hcl-widgets-reparse', 'hcl-list')
  }
}

watch(
  bucket,
  () => {
    updatePagerFromBucket()
    updateSelectionfromBucket()

    nextTick(() => {
      triggerWidgetReparse()
    })
  },
  { deep: true }
)

/*
 * Row Handling
 */
const rowsPaged = computed((): DataListRow[] => {
  return pager.rowsCount > 0 ? bucket.value.rows : []
})

const rowIsVisible = (entity: DataListRowEntity, index: number) => {
  if (index === 0) return true
  return entity.entry.state.collapsible.parent === 'open'
}

const rowComponentName = (definition: any): any => {
  if (definition.type === 'html') return HclListRowHtml
  if (definition.type === 'list') return HclListRowList

  return HclListRowEmpty
}

const rowClasses = (entity: DataListRowEntity, index: number) => {
  const cssDefinition = (props.definition.content[index].css ?? '')
  const cssData = (entity.entry.css ?? '')

  if (cssDefinition !== '' || cssData !== '') {
    return [cssDefinition, cssData].join(' ')
  }

  return ''
}

const rowSubDefinitions = computed(() => {
  if (props.definition.content.length <= 1) return []

  const r = [] as any[]

  for (const i in props.definition.content) {
    r.push({
      name: props.definition.content[i].name,
      entity: props.definition.content[i].entity,
      type: props.definition.content[i].type,
      index: i,
      css: props.definition.content[i].configuration?.rows?.css ?? [],
    })
  }

  return r.slice(1, r.length)
})

const rowMessageSuppress = computed(() => {
  return props.definition.content[0].configuration.rows.messages?.suppress ?? false
})

const rowMessageNoRowsSearchActive = computed(() => {
  return props.definition.content[0].configuration.rows.messages?.empty ?? 'No results found'
})

const rowMessageNoRowsSearchInactive = computed(() => {
  return props.definition.content[0].configuration.rows.messages?.nosearchactive ?? 'Please start a search'
})

const rowMaxActionsCount = computed(() => {
  let max = 0
  for (const row of rowsPaged.value) {
    const actionsCount = row?.entities[0]?.entry?.values['actionArray']?.length ?? 0
    if (actionsCount > max) {
      max = actionsCount
    }
  }

  return max
})

/*
 * Row Collapsible Handling
 */
const {
  columnBodyCollapsibleOnToggle,
} = useCollapsibleList(props.definition.meta.id)

const collapsibleButtonShow = (row: DataListRow) => {
  return (row.entities[0].entry?.configuration?.collapsible?.target ?? -1) > 0
}

const collapsibleButtonStateIsOpen = (row: DataListRow) => {
  return (row.entities[0].entry?.state?.collapsible?.state ?? 'closed') === 'open'
}

const collapsibleButtonStateIsOpenMessage = computed(() => {
  return props.definition.content[0].configuration.collapsible?.open ?? 'Schliessen'
})

const collapsibleButtonStateIsClosedMessage = computed(() => {
  return props.definition.content[0].configuration.collapsible?.closed ?? 'Öffnen'
})

/*
 * Header Handling
 */
const columnHeaderShowHeader = computed(() => {
  return props.definition.content[0].configuration.header?.show ?? true
})

/*
 * Column Handling
 */
const {
  columnsRef,
  columnHeaderOnDragStart,
  columnHeaderOnDragMove,
  columnHeaderOnDragEnd,
  columnVisible,
  columnSorting,
  columnHeaderComponentName,
  columnHeaderOnSortChange,
  columnsVisibleCount
} = useColumns(
  props.definition.meta.id,
  props.definition.content[0].columns,
  props.definition.content[0].configuration,
  pager
)

provide('columnsRef', columnsRef)

const columnVisible2 = (column: DefinitionListContentColumn) => {
  if (column.type === 'stack' && column.hidden) return false

  if (column.type === 'stack' && column.items.length > 0) {
    return !column.items.every((column) => {
      return column.hidden
    })
  }

  if (column.type === 'checkbox' || column.type === 'checkbox-selection')
    return column.name === props.definition.content[0].configuration.rows.columns.selection.column
      ? bucket.value.selection.enabled
      : !column.hidden

  return !column.hidden
}

const columnKey = (column: DefinitionListContentColumn) => {
  return column.type + '[' + column.index + ']'
}

const columnClass = (column: DefinitionListContentColumn) => {
  const classes = ['row-column']

  classes.push('column-' + column.type)

  if (column.type === 'image-full-height') classes.push('column-body-padding-none')

  if (column.css && column.css.length > 0) {
    for (const cssClass of column.css) {
      classes.push(cssClass)
    }
  }

  return classes.join(' ')
}

/*
 * Header and Footer Handling
 */
const componentsToolbars = (toolbars: DefinitionToolbar[] | undefined): DefinitionToolbar[] => {
  if (toolbars === undefined) return []
  return toolbars
}

const onButtonClicked = (event: EventButtonClicked) => {
  emit('hcl-button-clicked', event)
}

/*
 * Selection
 */
const actionlist = useActionLists()

const updateListSelection = (targetState: string) => {
  actionlist.updateBucketEntitySelection(
    bucket.value,
    props.definition.meta.id,
    props.definition.content[0].configuration.rows.columns.selection.column,
    {
      companyState: targetState,
      projectState: targetState,
      applicantState: targetState,
      employeeState: targetState
    }
  )
}

const onListGlobalSelectAllChanged = (event: EventListGlobalSelectAllChanged) => {
  if (event.selection === globalSelectAll.value) return

  globalSelectAll.value = event.selection

  if (!bucketSelection.value) return

  bucketSelection.value.header.selected = globalSelectAll.value

  const targetState = globalSelectAll.value ? 'on' : 'off'

  updateListSelection(targetState)
}

const columnHeaderOnCheckboxSelectionChange = (event: EventColumnCheckboxToggle) => {
  if (!event.checked) globalSelectAll.value = false

  if (!bucketSelection.value) return

  bucketSelection.value.header.selected = event.checked
}

const onListRowCheckboxChanged = (event) => {
  if (event.name === props.definition.content[0].configuration.rows.columns.selection.column && event.checked === false) {
    globalSelectAll.value = false
  }
}

const rowsSelectedPageAll = computed(() => {
  return bucketSelection.value?.header?.selected ?? false
})

const rowsSelectedIds = computed(() => {
  if (!bucket.value.rows) return []

  const rowsSelected = bucket.value.rows.filter((row: DataListRow) => {
    return row.entities[0].entry.values[props.definition.content[0].configuration.rows.columns.selection.column].value === 'on'
  })

  if (rowsSelected.length === 0) return []

  const ids: (string | number)[] = []
  for (const row of rowsSelected) {
    ids.push(row.entities[0].entry.values[props.definition.content[0].configuration.identity.name])
  }

  return ids
})

const rowsSelectedIdsFlattened = computed(() => {
  return rowsSelectedIds.value.join(',')
})

const rowsSelectedCount = computed(() => {
  return rowsSelectedIds.value.length
})

const toolbarData = computed(() => {
  return {
    // Selection
    selection: {
      listMetaId: props.definition.meta.id,
      columnIdentity: props.definition.content[0].configuration.rows.columns.selection.column,
      global: globalSelectAll.value,
      page: rowsSelectedPageAll.value
    },
    // ToolbarContentPager
    pager: {
      parentid: props.definition.meta.id,
      pagination: props.definition.content[0].configuration.pagination,
      pager: pager,
      sorting: bucket.value.sorting,
    },
    // ToolbarContentHeader
    rows: {
      count: pager.rowsCount
    },
    rowsSelected: {
      globalAll: globalSelectAll.value,
      globalAllAsText: globalSelectAll.value ? 'true' : 'false',
      pageAll: rowsSelectedPageAll.value,
      pageAllAsText: rowsSelectedPageAll.value ? 'true' : 'false',
      ids: rowsSelectedIds.value,
      idsFlattened: rowsSelectedIdsFlattened.value,
      count: rowsSelectedCount.value
    }
  }
})

/*
 * Loading
 */
const blockMetaId: string = inject('blockMetaId', '')

const { getBlockGroupNameValueWithDefault } = useStateStore()

const loading = getBlockGroupNameValueWithDefault(
  blockMetaId,
  'global',
  'loading',
  false
)

const onHclListEmbeddedUpdated = () => {
  generateCacheSkeletonRowsHeights(refSkeletonRows.value)
}
</script>

<style lang="scss">
.hcl-list {
  .list-header:not(:empty) {
    padding-bottom: 0.5rem;
  }

  .list-footer:not(:empty) {
    padding-top: 0.5rem;
  }

  .list-content {
    .content-wrapper {
      overflow-x: auto;

      .table-header {
        .header-row {
          .row-column {
            color: var(--theme-color-interaction, '#0c3e94');
            vertical-align: top;
            border-top: none;
            border-bottom: none;

            .column-entry {
              display: flex;

              .entry-sorter {
                cursor: pointer;

                .entry-sorter-icon {
                  padding-left: 0.25rem;
                  height: 1.25rem;
                  width: 1.25rem;

                  &.icon-sort-none {
                    opacity: 0.1;
                  }

                  &.icon-sort-none:hover {
                    opacity: 0.8;
                  }
                }
              }
            }

            &.column-moveable {
              cursor: move;
            }

            &.column-no-wrap {
              white-space: nowrap;
            }
          }
        }
      }

      .row-disable-border {
        .row-column {
          border: none;
        }
      }

      .row-column {
        .wrapper {
          display: flex;
        }

        .column-checkbox-selection,
        .column-checkbox {
          width: 1.25rem;
        }

        .column-collapsible {
          display: flex;
          flex-grow: 1;
          justify-content: flex-end;
          width: 2.5rem;

          .btn {
            color: var(--theme-color-interaction, '#0c3e94');
          }
        }

        .column-tags {
          margin-bottom: 0.25rem;

          .tags-tag {
            padding: 0.25rem 0.5rem;
          }

          :not(:last-child) {
            margin-right: 0.5rem;
          }
        }

        .column-badge {
          height: 100%;
          padding: 0.5rem 0.75rem;

          i[class*='eii--']:not([class*=eii--stack]) {
            top: 0;
          }
        }

        .column-status {
          margin-bottom: 0.25rem;
        }

        .column-donut {
          &.cdc-container:hover {
            cursor: pointer;
          }
        }
      }
    }
  }
}
</style>
